import type { ConsentType } from "./hooks"

// Telehealth consent copy for the consumer booking flow (MEM-755 / MEM-782).
//
// Literal strings (no react-intl): this package isn't in the intl extraction pipeline and the
// experience is English-only for the foreseeable future. Keyed by consent type so each consent's copy
// is unambiguous.
//
// ⚠ This is legal consent copy — change it deliberately. Consent signoffs are recorded with only a
// timestamp (no stored version), so "what a member agreed to" is reconstructed from this file's
// git/deploy history at their signedAt. Bodies are verbatim legal documents — telehealth informed
// consent from "8.26 Carrot Medical Telehealth Consent", NPP from "Carrot Medical NPP (August 31st)"
// (two lines unbolded to match Figma), HIPAA from "Carrot Medical — HIPAA Authorization Form
// (August 31st)" (section headings sentence-cased to match Figma; its "My name:" line is interpolated
// with the member's legal name at render). Copy is modeled as semantic blocks so ConsentPage can render
// real <ul>/<li>/<p> (WCAG 1.3.1): a paragraph, or a list whose items may own nested blocks.

// A run of body copy. `bold` marks an inline emphasis span (defined terms, headings, lead-ins).
// `href` turns the span into an external link (opens in a new tab); `ariaLabel` gives it an accessible
// name when the visible text alone ("here") wouldn't convey the destination.
export type ConsentSpan = { text: string; bold?: boolean; href?: string; ariaLabel?: string }
// A standalone paragraph of copy.
type ConsentParagraph = { type: "paragraph"; spans: ConsentSpan[] }
// A list item: its own line of copy plus any owned sub-blocks (an explanatory paragraph, a nested list).
type ConsentListItem = { spans: ConsentSpan[]; blocks?: ConsentBlock[] }
// A bulleted list rendered as a semantic <ul>.
type ConsentList = { type: "list"; items: ConsentListItem[] }
// One top-level piece of a consent's body.
export type ConsentBlock = ConsentParagraph | ConsentList

type ConsentEntry = {
  // The read-only copy shown in the scrollable box: a heading (this consent's name) plus body blocks.
  section: { heading: string; body: ConsentBlock[] }
  // The acknowledgement checkbox (NPP, HIPAA). Absent for telehealth informed consent, which is
  // ratified by the typed-name signature instead.
  clause?: { label: string }
}

export type ConsentDocument = {
  // Constant flow title (H1); ConsentPage appends "(X of N)". Each consent's own name is its section heading.
  title: string
  intro: string
  // Every consent type must have copy — a missing one fails to compile.
  consents: Record<ConsentType, ConsentEntry>
  // The typed-name signature ratifies the telehealth informed consent + consent to treat (page 1 only).
  // Per MEM-755 the typed name is client-side gating only — never sent or stored. `label` is the only
  // text below the consent box.
  signature: { label: string }
}

// Token in HIPAA copy that ConsentPage replaces with the member's legal name at render time.
export const PATIENT_NAME_PLACEHOLDER = "{{name}}"

export const consentDocument: ConsentDocument = {
  title: "Consent to telehealth services",
  intro: "Please read and agree to the following before booking your visit.",
  consents: {
    telehealth_informed_consent: {
      section: {
        heading: "Telehealth informed consent and Consent to treat",
        body: [
          { type: "paragraph", spans: [{ text: "Informed Consent to Telehealth Services", bold: true }] },
          {
            type: "paragraph",
            spans: [
              { text: "DO NOT USE THIS SERVICE IF YOU MAY BE EXPERIENCING A MEDICAL EMERGENCY.", bold: true },
              {
                text: " In an emergent situation, you can: (i) call 911; (ii) go to the nearest emergency room; (iii) contact your local crisis center; (iv) if applicable, call the National Suicide Prevention Lifeline (1-800-273-8255); or (v) if applicable, contact the Crisis Text Line (text “HOME” to 741-741)."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "I hereby give my informed consent for medical treatment and procedures to be administered by the healthcare professionals at Carrot Medical of Delaware, LLC and its affiliated professional entities (collectively  “"
              },
              { text: "Carrot Medical", bold: true },
              { text: "”)" },
              { text: ". ", bold: true },
              { text: "By agreeing to this informed consent (“" },
              { text: "Consent", bold: true },
              { text: "”), you have elected to receive services via telehealth from Carrot Medical (“" },
              { text: "Services”", bold: true },
              {
                text: "). If you have questions about a provider’s credentials and professional background, please ask your Carrot Medical provider. All capitalized terms used in this Consent but not defined herein will have the meanings provided in the Notice of Privacy Practices. Only use the Services if you have read this information and subsequently made an informed decision that the Services are right for you. If you have any questions, please email us at "
              },
              { text: "legal@get-carrot.com", bold: true },
              { text: "." }
            ]
          },
          { type: "paragraph", spans: [{ text: "I understand and acknowledge the following:" }] },
          {
            type: "list",
            items: [
              {
                spans: [{ text: "Nature of Consent:", bold: true }],
                blocks: [
                  {
                    type: "paragraph",
                    spans: [
                      {
                        text: "I understand that by signing this form, I am authorizing Carrot Medical and its healthcare providers to provide medical treatment, conduct diagnostic tests, and perform necessary procedures to diagnose and treat my medical condition."
                      }
                    ]
                  }
                ]
              },
              {
                spans: [{ text: "Nature of Treatment:", bold: true }],
                blocks: [
                  {
                    type: "paragraph",
                    spans: [
                      {
                        text: "I acknowledge that Carrot Medical may employ a variety of medical treatments, including but not limited to examinations, diagnostic tests, medical procedures, and administration of medication. I understand that alternative treatments, risks, and potential complications will be discussed with me before any procedures are performed."
                      }
                    ]
                  }
                ]
              },
              {
                spans: [{ text: "Telehealth:", bold: true }],
                blocks: [
                  {
                    type: "paragraph",
                    spans: [
                      {
                        text: "Telehealth services involve interactive video conferencing equipment and devices that let my health care provider deliver health care services to me from a location that is different than my location. I confirm that I have read this form (or had it explained to me) and understand the following:"
                      }
                    ]
                  },
                  {
                    type: "list",
                    items: [
                      {
                        spans: [
                          {
                            text: "I will not be physically in the same room as my health care provider during the visit. I will be told about and asked for my consent before any other Carrot Medical staff or trainees actively assist my health care provider during this visit."
                          }
                        ]
                      },
                      {
                        spans: [
                          {
                            text: "There are risks and consequences associated with telehealth services. These include, but are not limited to:"
                          }
                        ],
                        blocks: [
                          {
                            type: "list",
                            items: [
                              { spans: [{ text: "disruption of the appointment caused by technology failures;" }] },
                              {
                                spans: [
                                  {
                                    text: "the limited ability of my health care provider to respond to emergencies during the visit;"
                                  }
                                ]
                              },
                              {
                                spans: [
                                  {
                                    text: "interruption or violations of confidentiality by people who do not have permission to be at the visit. While Carrot Medical takes all reasonable steps to secure telehealth visits and breaches are very rare, no technology is completely secure; and"
                                  }
                                ]
                              },
                              {
                                spans: [
                                  {
                                    text: "the chance that I or my health care provider decides that the video conferencing equipment or connection is not adequate for this visit and decides to stop the telemedicine visit, switch to an alternate Carrot Medical-approved video conferencing service, or make other arrangements to continue the visit;"
                                  }
                                ]
                              },
                              {
                                spans: [
                                  {
                                    text: "the chance that during a telehealth visit, my provider may not identify medical conditions that otherwise may be identified during an in-person visit."
                                  }
                                ]
                              }
                            ]
                          }
                        ]
                      },
                      {
                        spans: [
                          {
                            text: "I have the right to refuse to participate or stop participating in a telehealth visit, and my refusal will be written in my medical record. I understand that my refusal my impact my right to future care or treatment at Carrot Medical."
                          }
                        ]
                      },
                      {
                        spans: [
                          {
                            text: "The laws that protect the privacy and confidentiality of my health care information apply to telehealth services."
                          }
                        ]
                      },
                      {
                        spans: [
                          {
                            text: "My health care information may be shared with other individuals for scheduling purposes."
                          }
                        ]
                      },
                      {
                        spans: [
                          {
                            text: "I understand that during the telehealth visit, I must be physically located in a state where my provider is licensed, or my provider will not be able to conduct the visit, and it will need to be rescheduled. If I am in a state other than the state I previously provided at the time of my visit, I will tell the provider’s office before the visit to confirm that they can see me."
                          }
                        ]
                      },
                      {
                        spans: [
                          {
                            text: "I understand that my provider may be a physician, nurse practitioner, physician assistant, or other licensed health care professional acting within the scope of their license, I understand that Carrot Medical will make information about my provider's identity, credentials, and professional background available to me, and that I may ask my provider about their credentials before or during my consultation."
                          }
                        ]
                      },
                      {
                        spans: [
                          {
                            text: "I have had all my questions about this telehealth service answered to my satisfaction. The risks, benefits, and alternatives to telehealth visits have been shared with me in a language I understand."
                          }
                        ]
                      }
                    ]
                  }
                ]
              },
              {
                spans: [{ text: "Risks and Benefits:", bold: true }],
                blocks: [
                  {
                    type: "paragraph",
                    spans: [
                      {
                        text: "I understand that all medical treatments and procedures carry certain risks and potential benefits. While Carrot Medical will take necessary precautions to minimize risks, I acknowledge that no guarantees or assurances can be made regarding the outcome of any treatment or procedure. Possible risks include delays in evaluation and treatment could occur due to deficiencies or failures of the equipment and technologies, and in rare events, our provider may determine that the transmitted information is of inadequate quality, thus necessitating a rescheduled telehealth consult or a meeting with my local primary care doctor. There are various benefits associated with telehealth services, including improved access to care by enabling me to remain in my home while the provider consults with me, more efficient care evaluation and management, and obtaining expertise of a specialist as appropriate. I understand that I may expect the anticipated benefits from the use of telehealth in my care, but that no results can be guaranteed or assured."
                      }
                    ]
                  }
                ]
              },
              {
                spans: [{ text: "Privacy and Confidentiality:", bold: true }],
                blocks: [
                  {
                    type: "paragraph",
                    spans: [
                      {
                        text: "I acknowledge that I have been provided with, or given access to, Carrot Medical's Notice of Privacy Practices, which describes how my protected health information may be used and disclosed. I acknowledge that Carrot Medical is committed to protecting the privacy and confidentiality of my personal health information in accordance with applicable laws and regulations. I authorize the collection, use, and disclosure of my health information for the purposes of treatment, payment, and healthcare operations."
                      }
                    ]
                  }
                ]
              },
              {
                spans: [{ text: "Right to Refuse or Withdraw Consent:", bold: true }],
                blocks: [
                  {
                    type: "paragraph",
                    spans: [
                      {
                        text: "This Consent takes effect when I accept it and remains in effect until I revoke it. I understand that this Consent will be considered renewed each time I initiate a new telehealth consultation with a Provider. I understand that withdrawing this Consent may affect my ability to receive the Services."
                      }
                    ]
                  },
                  {
                    type: "paragraph",
                    spans: [
                      {
                        text: "I can refuse or withdraw my consent for medical treatment at any time and my withdrawal will apply prospectively and will not affect care already provided. I understand that this decision may have consequences and that I should discuss any concerns or questions with my healthcare provider."
                      }
                    ]
                  }
                ]
              },
              {
                spans: [{ text: "Communication and Follow-up:", bold: true }],
                blocks: [
                  {
                    type: "paragraph",
                    spans: [
                      {
                        text: "I understand the importance of open and honest communication with my healthcare provider. I agree to provide accurate and complete information about my medical history, current medications, allergies, and other relevant details. I understand I should follow any post-treatment instructions and attend follow-up appointments as recommended."
                      }
                    ]
                  }
                ]
              },
              {
                spans: [{ text: "Authorization for Medical Decision-Making:", bold: true }],
                blocks: [
                  {
                    type: "paragraph",
                    spans: [
                      {
                        text: "I authorize Carrot Medical and its healthcare providers to make necessary medical decisions on my behalf if I cannot do so, based on their professional judgment and in accordance with applicable laws and regulations."
                      }
                    ]
                  }
                ]
              },
              {
                spans: [{ text: "Choice of Pharmacy Services:", bold: true }],
                blocks: [
                  {
                    type: "paragraph",
                    spans: [
                      {
                        text: "If I receive a prescription as a result of my use of the Services, I may choose to have my prescription fulfilled through the pharmacy of my choice. I give Carrot Medical consent to send and disclose to the pharmacy of my choice all information provided by me, health care records, and other applicable health care information and personal information (such as my name, location and demographic information) so that I may receive pharmacy services."
                      }
                    ]
                  }
                ]
              },
              {
                spans: [{ text: "Additional Patient Consents:", bold: true }],
                blocks: [
                  {
                    type: "list",
                    items: [
                      {
                        spans: [
                          {
                            text: "If I am experiencing a medical emergency, I will be directed to dial 9-1-1 immediately and my Provider is not able to connect me directly to any local emergency services."
                          }
                        ]
                      },
                      {
                        spans: [
                          {
                            text: "I may elect to seek services from a medical group or provider with in-person clinics as an alternative to receiving telehealth services."
                          }
                        ]
                      },
                      {
                        spans: [
                          {
                            text: "I have the right to withhold or withdraw my consent to the use of telehealth in the course of my care at any time without affecting my right to future care or treatment."
                          }
                        ]
                      },
                      {
                        spans: [
                          {
                            text: "Federal and state law requires health care providers to protect the privacy and the security of health information. I am entitled to all confidentiality protections under applicable federal and state laws. I understand all medical reports resulting from the telehealth visit are part of my medical record. Carrot Medical will take steps to make sure that my health information is protected in accordance with applicable laws."
                          }
                        ]
                      },
                      {
                        spans: [
                          {
                            text: "Telehealth may involve electronic communication of my personal health information to other health practitioners who may be located in other areas, including out of state."
                          }
                        ]
                      },
                      {
                        spans: [
                          {
                            text: "There is no guarantee that I will be treated by a Medical Group provider. I understand that I will not be prescribed any narcotics, nor is there any guarantee that I will be given a prescription at all."
                          }
                        ]
                      },
                      {
                        spans: [
                          {
                            text: " My provider reserves the right to deny care for potential misuse of the Services or for any other reason if, in the professional judgment of my provider, the provision of the Services is not medically or ethically appropriate"
                          }
                        ]
                      }
                    ]
                  }
                ]
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "Agreement and Consent:", bold: true }] },
          {
            type: "paragraph",
            spans: [
              {
                text: "By accepting this Telehealth Consent, I acknowledge and attest that I have read and understood the contents of this Medical Consent Form, and I voluntarily consent to receive medical treatment and procedures from Carrot Medical.",
                bold: true
              }
            ]
          },
          {
            type: "paragraph",
            spans: [{ text: "Additional State-Specific Consents and Disclosures", bold: true }, { text: "." }]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "The following consents apply to patients accessing Carrot Medical’s services for the purposes of participating in a telehealth consultation as required by the states listed below:"
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Alaska: ", bold: true },
              {
                text: "I understand that my primary care provider may obtain a copy of my records of my telehealth encounter. I have been informed that if I want to register a formal complaint about a provider, I should visit the medical board’s website, "
              },
              {
                text: "here",
                href: "https://www.commerce.alaska.gov/web/cbpl/ComplaintFAQs.aspx",
                ariaLabel: "Alaska state medical board website (opens in a new tab)"
              },
              { text: "." }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Arizona: ", bold: true },
              {
                text: "I understand I am entitled to all existing confidentiality protections pursuant to A.R.S. § 12- 2292. I also understand dissemination of any images or information identifiable to me for research or educational purposes shall not occur without my consent, unless authorized by state or federal law."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "California: ", bold: true },
              { text: "I understand that some or all of my services may be provided using telehealth." }
            ]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "I consent to the sharing of my health information among Carrot Medical's affiliated entities and care team members for the purpose of coordinating my care, consistent with the California Confidentiality of Medical Information Act (CMIA). All physicians licensed to practice in the State of California are licensed and regulated by the Medical Board of California. To check on a physician’s license or to file a complaint, go to "
              },
              { text: "www.mbc.ca.gov", href: "https://www.mbc.ca.gov" },
              { text: ", email licensecheck@mbc.ca.gov, call (800) 633-2322." }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Colorado: ", bold: true },
              {
                text: "I consent to the use of telehealth. I understand that I will not be charged separately for the use of telehealth technology and that my privacy will be protected under Colorado law."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Connecticut", bold: true },
              {
                text: ": I understand that my primary care provider may obtain a copy of my records and my telehealth encounter."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "D.C.: I have been informed of alternate forms of communication between me and a physician for urgent matters.  Relevant communications with the physician, including those done via electronic methods shall be documented and filed in my medical record."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Florida", bold: true },
              {
                text: ": To view my rights under Florida’s Patient Bill of Rights and Responsibilities, I should visit the Florida Agency for Health Care Administration or click "
              },
              {
                text: "here",
                href: "https://www.flsenate.gov/Laws/Statutes/2024/381.026",
                ariaLabel: "Florida Patient Bill of Rights and Responsibilities (opens in a new tab)"
              },
              {
                text: ". To view my rights under Florida’s Weight-Loss Consumer Bill of Rights, I should visit "
              },
              {
                text: "here",
                href: "https://www.flsenate.gov/Laws/Statutes/2024/501.0575",
                ariaLabel: "Florida Weight-Loss Consumer Bill of Rights (opens in a new tab)"
              },
              { text: "." }
            ]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "I understand that my provider may not be physically located in Florida when telehealth services are provided. I consent to receive these services under Florida's Telehealth Practice Act."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Georgia: ", bold: true },
              {
                text: "I  have been given clear, appropriate, accurate instructions on follow-up in the event of needed emergent care related to the treatment."
              },
              { text: "  ", bold: true }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Hawaii: ", bold: true },
              {
                text: "I consent to the use of telehealth services as permitted by Hawaii law. I understand that my privacy will be protected under state and federal law and that I may withdraw consent at any time."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Idaho", bold: true },
              {
                text: ": I have been informed that if I want to register a formal complaint about a provider, I should visit the medical board’s website, "
              },
              {
                text: "here",
                href: "https://dopl.idaho.gov/filing-a-complaint/",
                ariaLabel: "Idaho state medical board website (opens in a new tab)"
              },
              { text: "." }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Illinois: ", bold: true },
              { text: "I understand that telehealth may be used for parts of my care. Participation is voluntary." }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Indiana", bold: true },
              {
                text: ": I have been informed that if I want to register a formal complaint about a provider, I should visit the medical board’s website, "
              },
              {
                text: "here",
                href: "https://www.in.gov/pla/licensure-discipline/report-a-professional",
                ariaLabel: "Indiana state medical board website (opens in a new tab)"
              },
              { text: "." }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Iowa: ", bold: true },
              {
                text: "I have been informed that if I want to register a formal complaint about a provider, I should visit the medical board’s website, "
              },
              {
                text: "here",
                href: "https://medicalboard.iowa.gov/consumers/filing-complaint",
                ariaLabel: "Iowa state medical board website (opens in a new tab)"
              },
              { text: "." }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Kansas", bold: true },
              {
                text: ": I understand that if I have a primary care provider or other treating physician, the person providing telemedicine services must send a report to such primary care or other treating physician of the treatment and services rendered to me within three days of me providing consent to send such report."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Kentucky: ", bold: true },
              {
                text: "I have been informed that if I want to register a formal complaint about a provider, I should visit the medical board’s website, "
              },
              {
                text: "here",
                href: "https://kbml.ky.gov/grievances/Pages/default.aspx",
                ariaLabel: "Kentucky state medical board website (opens in a new tab)"
              },
              { text: "." }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Louisiana: ", bold: true },
              {
                text: "I understand the role of other health care providers that may be present during the consultation other than the telehealth provider"
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Maine: ", bold: true },
              {
                text: "I have been informed that if I want to register a formal complaint about a provider, I should visit the medical board’s website, "
              },
              {
                text: "here",
                href: "https://www.maine.gov/md/complaint/file-complaint",
                ariaLabel: "Maine state medical board website (opens in a new tab)"
              },
              { text: "; or, the Maine Board of Osteopathic Licensure’s website, " },
              {
                text: "here",
                href: "https://www.maine.gov/osteo/contact",
                ariaLabel: "Maine Board of Osteopathic Licensure website (opens in a new tab)"
              },
              { text: "." }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "New Jersey: ", bold: true },
              {
                text: "I understand I have the right to request a copy of my medical information and I understand my medical information may be forwarded directly to my primary care provider or health care provider of record, or upon my request, to other health care providers. If I do not have a primary care provider or other health care provider of record, the health care provider engaging in telemedicine or telehealth may advise me to contact a primary care provider, and, upon request by me,  may assist me with locating a primary care provider or other in-person medical assistance that, to the extent possible, located within reasonable proximity to me."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "New Hampshire", bold: true },
              {
                text: ": I understand that my primary care provider or treating provider may obtain a copy of my records of my telehealth encounter."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [{ text: "New York: ", bold: true }, { text: "I consent to receive telehealth services." }]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "I understand that all communications will be secure and that my information will remain confidential under New York law, including New York Public Health Law Article 27-F."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Ohio: ", bold: true },
              {
                text: "I understand that my primary care provider may obtain a copy of my records of my telehealth encounter."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Oklahoma: ", bold: true },
              {
                text: "I have been informed that if I want to register a formal complaint about a provider, I should visit the medical board’s website, "
              },
              {
                text: "here",
                href: "https://www.okmedicalboard.org/complaint",
                ariaLabel: "Oklahoma state medical board website (opens in a new tab)"
              },
              { text: "; or, the Oklahoma Board of Osteopathic Examiners’ website, " },
              {
                text: "here",
                href: "https://oklahoma.gov/osboe/forms.html",
                ariaLabel: "Oklahoma Board of Osteopathic Examiners website (opens in a new tab)"
              },
              { text: "." }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Oregon: ", bold: true },
              {
                text: "I consent to receive telehealth services under Oregon law. If I have a concern or complaint about the providers  providing care to me, I may contact a board agency to assist me. I understand that the provider may ask if I need more information."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Rhode Island: ", bold: true },
              {
                text: "If I use e-mail or text-based technology to communicate with my Carrot Medical provider, then I understand the types of transmissions that will be permitted and the circumstances when alternate forms of communication should be utilized. I have also discussed security measures, such as encryption of data, password protected screen savers and data files, or utilization of other reliable authentication techniques, as well as potential risks to privacy. I have been informed that if I want to register a formal complaint about a provider, I should visit the medical board’s website, "
              },
              {
                text: "here",
                href: "https://health.ri.gov/complaints/",
                ariaLabel: "Rhode Island state medical board website (opens in a new tab)"
              },
              { text: "." }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "South Dakota:", bold: true },
              {
                text: " I have received disclosures regarding the delivery models and treatment methods or limitations. You have discussed with the telehealth provider the diagnosis and its evidentiary basis, and the risks and benefits of various treatment options. "
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "South Carolina", bold: true },
              {
                text: ": I understand that my medical records may be distributed only with my consent and in accordance with applicable laws and regulations to other treating health care practitioners."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Texas: ", bold: true },
              {
                text: "I understand that with my consent, medical records may be sent to my primary care physician within 72 hours after receiving services."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "I consent to the use of telecommunication technology, including telephone or electronic communications."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "I understand that these services will comply with Texas Medical Board telemedicine standards and that I may decline telehealth at any time."
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "I have also been informed of the following notice:" }] },
          {
            type: "paragraph",
            spans: [
              {
                text: "NOTICE CONCERNING COMPLAINTS -Complaints about physicians, as well as other licensees and registrants of the Texas Medical Board, including physician assistants, acupuncturists, and surgical assistants may be reported for investigation at the following address: Texas Medical Board, Attention: Investigations, 333 Guadalupe, Tower 3, Suite 610,"
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "P.O. Box 2018, MC-263, Austin, Texas 78768-2018, Assistance in filing a complaint is available by calling the following telephone number: 1-800-201-9353, For more information, please visit "
              },
              { text: "www.tmb.state.tx.us", href: "https://www.tmb.state.tx.us" },
              { text: "." }
            ]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "AVISO SOBRE LAS QUEJAS- Las quejas sobre médicos, asi como sobre otros profesionales acreditados e inscritos del Consejo Médico de Tejas, incluyendo asistentes de médicos, practicantes de acupuntura y asistentes de cirugia, se pueden presentar en la siguiente dirección para ser investigadas: Texas Medical Board, Attention: Investigations, 333 Guadalupe, Tower 3, Suite 610, P.O. Box 2018, MC-263, Austin, Texas 78768-2018, Si necesita ayuda para presentar una queja, llame al: 1-800-201-9353, Para obtener más información, visite nuestro sitio web en "
              },
              { text: "www.tmb.state.tx.us", href: "https://www.tmb.state.tx.us" },
              { text: "." }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Utah", bold: true },
              {
                text: ": I understand (i) to whom my health information may be disclosed and for what purpose, and have received information on any consent governing release of my patient-identifiable information to a third-party; and (ii) my rights with respect to patient health information; I was warned of: potential risks to privacy notwithstanding the security measures and that information may be lost due to technical failures, and agree to hold Provider harmless for such loss. I have been provided with the location of Carrot Medical’s website and contact information. I am able to a (i) access, supplement, and amend my patient-provided personal health information; and (ii) request a transfer to another provider of my medical record documenting the telemedicine services."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Virginia: ", bold: true },
              {
                text: "I agree to hold harmless Carrot Medical for information lost due to technical failures; and I provide my express consent to forward patient-identifiable information to a third party. I acknowledge that I have received details on security measures taken with the use of telemedicine services, such as encrypting date of service, password protected screen savers, encrypting data files, or utilizing other reliable authentication techniques, as well as potential risks to privacy notwithstanding such measures"
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "I consent to the use of telemedicine, consistent with Virginia law. I understand that telehealth services will be conducted securely and documented in my record."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Vermont", bold: true },
              {
                text: ": I understand that I have the right to receive a consult with a distant-site provider and will receive one upon request immediately or within a reasonable time after the results of the initial consult. I understand that receiving telemedicine services via Carrot Medical does not preclude me from receiving real-time telemedicine or face-to-face services with the distant provider at a future date."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "I have been informed that if I want to register a formal complaint about a provider, I should visit the Vermont Board of Medical Practice website, "
              },
              {
                text: "here",
                href: "https://www.healthvermont.gov/health-professionals-systems/board-medical-practice/file-complaint",
                ariaLabel: "Vermont Board of Medical Practice website (opens in a new tab)"
              },
              { text: " or the Vermont Board of Osteopathic Examiners, " },
              {
                text: "here",
                href: "https://sos.vermont.gov/opr/complaints-conduct-discipline/",
                ariaLabel: "Vermont Board of Osteopathic Examiners website (opens in a new tab)"
              },
              { text: "." }
            ]
          }
        ]
      }
    },
    notice_of_privacy_practices: {
      section: {
        heading: "Notice of Privacy Practices",
        body: [
          { type: "paragraph", spans: [{ text: "Carrot Medical of Delaware, LLC", bold: true }] },
          { type: "paragraph", spans: [{ text: "Notice of Privacy Practices" }] },
          {
            type: "paragraph",
            spans: [
              {
                text: "THIS NOTICE DESCRIBES HOW MEDICAL INFORMATION ABOUT YOU MAY BE USED AND DISCLOSED AND HOW YOU CAN GET ACCESS TO THIS INFORMATION. PLEASE REVIEW IT CAREFULLY."
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "Your Rights", bold: true }] },
          {
            type: "paragraph",
            spans: [
              {
                text: "When it comes to your protected health information as defined by HIPAA (your health information), you have certain rights. This section explains your rights and some of our responsibilities to help you."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [{ text: "Get an electronic or paper copy of your medical record", bold: true }]
          },
          {
            type: "list",
            items: [
              {
                spans: [
                  {
                    text: "You can ask to see or get an electronic or paper copy of your medical record and other health information we have about you. Ask us how to do this."
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "We will provide a copy or a summary of your health information, usually within 30 days of your request. We may charge a reasonable, cost-based fee."
                  }
                ]
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "Ask us to correct your medical record", bold: true }] },
          {
            type: "list",
            items: [
              {
                spans: [
                  {
                    text: "You can ask us to correct health information about you that you think is incorrect or incomplete. Ask us how to do this."
                  }
                ]
              },
              {
                spans: [{ text: 'We may say "no" to your request, but we\'ll tell you why in writing within 60 days.' }]
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "Request confidential communications", bold: true }] },
          {
            type: "list",
            items: [
              {
                spans: [
                  {
                    text: "You can ask us to contact you in a specific way (for example, home, office, or cell phone) or to send mail to a different address."
                  }
                ]
              },
              { spans: [{ text: 'We will say "yes" to all reasonable requests.' }] }
            ]
          },
          { type: "paragraph", spans: [{ text: "Ask us to limit what we use or share", bold: true }] },
          {
            type: "list",
            items: [
              {
                spans: [
                  {
                    text: "You can ask us not to use or share certain health information for treatment, payment, or our operations."
                  }
                ]
              },
              { spans: [{ text: 'We are not required to agree to your request, and we may say "no".' }] },
              {
                spans: [
                  {
                    text: 'If you pay for a service or health care item out-of-pocket in full, you can ask us not to share that information for the purpose of payment or our operations with your health insurer. We will say "yes" unless a law requires us to share that information.'
                  }
                ]
              }
            ]
          },
          {
            type: "paragraph",
            spans: [{ text: "Get a list of those with whom we've shared information", bold: true }]
          },
          {
            type: "list",
            items: [
              {
                spans: [
                  {
                    text: "You can ask for a list (accounting) of the times we've shared your health information for six years prior to the date you ask, who we shared it with, and why."
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "We will include all the disclosures except for those about treatment, payment, and health care operations, and certain other disclosures (such as any you asked us to make). We'll provide one accounting a year for free but will charge a reasonable, cost-based fee if you ask for another one within 12 months."
                  }
                ]
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "Get a copy of this privacy notice", bold: true }] },
          {
            type: "list",
            items: [
              {
                spans: [
                  {
                    text: "You can ask for a paper copy of this notice at any time, even if you have agreed to receive the notice electronically. We will provide you with a paper copy promptly."
                  }
                ]
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "Choose someone to act for you", bold: true }] },
          {
            type: "list",
            items: [
              {
                spans: [
                  {
                    text: "If someone has authority to act as your personal representative, such as if someone has your medical power of attorney or if someone is your legal guardian, that person can exercise your rights and make choices about your health information."
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "We will make sure the person has this authority and can act for you before we take any action."
                  }
                ]
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "File a complaint if you feel your rights are violated", bold: true }] },
          {
            type: "list",
            items: [
              {
                spans: [
                  {
                    text: "You can complain if you feel we have violated your rights by contacting us using the contact information at the end of this notice."
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "You can file a complaint with the U.S. Department of Health and Human Services Office for Civil Rights. You can contact us using the information at the end of this notice if you need their contact information."
                  }
                ]
              },
              { spans: [{ text: "We will not retaliate against you for filing a complaint." }] }
            ]
          },
          { type: "paragraph", spans: [{ text: "Your Choices", bold: true }] },
          {
            type: "paragraph",
            spans: [
              {
                text: "For certain health information, you can tell us your choices about what we share. If you have a clear preference for how we share your information in the situations described below, talk to us. Tell us what you want us to do, and we will follow your instructions."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [{ text: "In these cases, you have both the right and choice to tell us to:", bold: true }]
          },
          {
            type: "list",
            items: [
              {
                spans: [
                  {
                    text: "Share information with your family, close friends, or others involved in your care or payment for your care."
                  }
                ]
              },
              {
                spans: [{ text: "Share information in a disaster relief situation." }],
                blocks: [
                  {
                    type: "paragraph",
                    spans: [
                      {
                        text: "If you are not able to tell us your preference, for example if you are unconscious, we may go ahead and share your information if we believe it is in your best interest. We may also share your information when needed to lessen a serious and imminent threat to health or safety."
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "In these cases, we never share your information unless you give us written authorization:",
                bold: true
              }
            ]
          },
          {
            type: "list",
            items: [
              {
                spans: [{ text: "Marketing", bold: true }, { text: " (as defined in the HIPAA rules) purposes." }],
                blocks: [
                  {
                    type: "paragraph",
                    spans: [
                      { text: "Example:", bold: true },
                      {
                        text: " We will not accept any payments from other organizations in exchange for making communications to you about products or services unless you have given us your authorization to do so or the communication is otherwise permitted by law. We may provide refill reminders or communicate with you about a drug or biologic that is currently prescribed to you without your authorization so long as any payment we receive for making the communication is reasonably related to our cost of making the communication. In addition, we may give you promotional gifts of nominal value without obtaining your written authorization."
                      }
                    ]
                  }
                ]
              },
              { spans: [{ text: "Sale of your health information (as defined in the HIPAA rules)." }] },
              { spans: [{ text: "Most sharing of psychotherapy notes." }] },
              {
                spans: [
                  {
                    text: "Sharing of information in substance use disorder program patient records about you, subject to federal law at 42 CFR part 2, or certain highly confidential health information about you that is protected by state laws under circumstances when the laws require your consent or authorization before the information is shared."
                  }
                ],
                blocks: [
                  {
                    type: "paragraph",
                    spans: [
                      { text: "Example:", bold: true },
                      {
                        text: " In all cases, including those described below, if we have substance use disorder program patient records about you, subject to 42 CFR part 2, we cannot use or share information in those records in civil, criminal, administrative, or legislative investigations or proceedings against you without (1) your consent or (2) a court order and a subpoena. In addition, HIV test results, genetic test results, and mental or behavioral health information are subject to extra privacy protections in some states."
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            type: "paragraph",
            spans: [{ text: "How Do We Typically Use or Share Your Health Information?", bold: true }]
          },
          {
            type: "paragraph",
            spans: [{ text: "We typically use or share your health information in the following ways." }]
          },
          { type: "paragraph", spans: [{ text: "Treat you", bold: true }] },
          {
            type: "paragraph",
            spans: [
              { text: "We can use your health information and share it with other professionals who are treating you." }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Example:", bold: true },
              { text: " A doctor treating you for an injury asks another doctor about your overall health condition." }
            ]
          },
          { type: "paragraph", spans: [{ text: "Our Health Care Operations", bold: true }] },
          {
            type: "paragraph",
            spans: [
              {
                text: "We can use and share your health information to run our practice, improve your care, and contact you when necessary."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Example:", bold: true },
              { text: " We use your health information to manage and administer our services to you." }
            ]
          },
          { type: "paragraph", spans: [{ text: "Session Recording and AI Scribes", bold: true }] },
          {
            type: "paragraph",
            spans: [
              {
                text: 'We may record telehealth sessions and use AI-assisted transcription tools (known as "AI scribes") to document your care. Recordings and transcriptions are used solely for clinical documentation and care coordination purposes and are treated as part of your protected health information. All such recordings and transcriptions are subject to the same privacy protections described in this notice.'
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Example:", bold: true },
              {
                text: " An AI tool automatically transcribes your telehealth visit with your provider so that an accurate clinical note can be generated without the provider having to type during your appointment."
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "Bill for your services", bold: true }] },
          {
            type: "paragraph",
            spans: [
              {
                text: "We can use and share your health information to bill and get payment from health plans or other entities."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Example:", bold: true },
              {
                text: " We may share your health information with your health insurance plan so it will pay for your services."
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "Business Associates", bold: true }] },
          {
            type: "paragraph",
            spans: [
              {
                text: "We may share your health information with our business associates, which are service providers that perform billing, administrative or other services for us. We contractually require our business associates to protect the privacy and security of your health information."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Example:", bold: true },
              {
                text: " We share your information with Carrot Fertility, Inc., which provides management and administrative services to us."
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "Health Information Exchanges" }] },
          {
            type: "paragraph",
            spans: [
              {
                text: "We participate in health information exchanges (HIEs) and similar services and may use these services as a method to share, request, and receive your health information with health care providers, health plans, public health departments and other participating health care organizations for treatment, payment, health care operations, and public health. Sharing information electronically is a faster, more coordinated way to get your health information from your other health care providers. HIE participants are required to meet rules that protect the privacy and security of your health information. If your medical record contains highly confidential health information that requires your authorization under applicable law to be shared, we will not share the information through an HIE until you provide authorization."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "You can opt-out of all HIEs and not have your health information shared through any HIE at any time, by contacting us using the contact information at the end of this notice."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "If you choose to opt out of having your information shared through an HIE, this will not affect your access to care from us."
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "How Else Can We Use or Share Your Health Information?", bold: true }] },
          {
            type: "paragraph",
            spans: [
              {
                text: "We are allowed or required to share your information in other ways – usually in ways that contribute to the public good, such as public health and research. We have to meet certain conditions in the law before we can share your information for these purposes."
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "Help with public health and safety issues", bold: true }] },
          {
            type: "paragraph",
            spans: [{ text: "We can share health information about you for certain situations such as:" }]
          },
          {
            type: "list",
            items: [
              { spans: [{ text: "Preventing disease" }] },
              { spans: [{ text: "Helping with product recalls" }] },
              { spans: [{ text: "Reporting adverse reactions to medications" }] },
              { spans: [{ text: "Reporting suspected abuse, neglect, or domestic violence" }] },
              { spans: [{ text: "Preventing or reducing a serious threat to anyone's health or safety" }] }
            ]
          },
          { type: "paragraph", spans: [{ text: "Following up on abnormal lab results", bold: true }] },
          {
            type: "paragraph",
            spans: [
              {
                text: "If your lab results are abnormal and we are unable to reach you by phone, Carrot Medical may contact your local health care provider and follow up with a letter to the address you have provided to us."
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "Do research", bold: true }] },
          { type: "paragraph", spans: [{ text: "We can use or share your information for health research." }] },
          { type: "paragraph", spans: [{ text: "Comply with the law", bold: true }] },
          {
            type: "paragraph",
            spans: [
              {
                text: "We will share information about you if state or federal laws require it, including with the Department of Health and Human Services if it wants to see that we're complying with federal privacy law."
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "Respond to organ and tissue donation requests", bold: true }] },
          {
            type: "paragraph",
            spans: [{ text: "We can share health information about you with organ procurement organizations." }]
          },
          { type: "paragraph", spans: [{ text: "Work with a medical examiner or funeral director", bold: true }] },
          {
            type: "paragraph",
            spans: [
              {
                text: "We can share health information with a coroner, medical examiner, or funeral director when an individual dies."
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "We can use or share health information about you:" }] },
          {
            type: "list",
            items: [
              { spans: [{ text: "For law enforcement purposes or with a law enforcement official" }] },
              { spans: [{ text: "With health oversight agencies for activities authorized by law" }] },
              {
                spans: [
                  {
                    text: "For special government functions such as military, national security, and presidential protective services"
                  }
                ]
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "Respond to lawsuits and legal actions", bold: true }] },
          {
            type: "paragraph",
            spans: [
              {
                text: "We can share health information about you in response to a court or administrative order, or in response to a subpoena."
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "Our Responsibilities", bold: true }] },
          {
            type: "list",
            items: [
              {
                spans: [
                  {
                    text: "We are required by law to maintain the privacy and security of your protected health information."
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "We will let you know promptly if a breach occurs that may have compromised the privacy or security of your information."
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "We must follow the duties and privacy practices described in this notice and give you a copy of it."
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "We will not use or share your information other than as described here unless you tell us we can in writing. If you tell us we can, you may change your mind at any time. Let us know in writing if you change your mind."
                  }
                ]
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "Changes to the Terms of This Notice", bold: true }] },
          {
            type: "paragraph",
            spans: [
              {
                text: "We can change the terms of this notice, and the changes will apply to all information we have about you. The new notice will be available upon request, in our office, and on our web site."
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "Privacy Official Contact Information:", bold: true }] },
          { type: "paragraph", spans: [{ text: "HIPAA Compliance Officer" }] },
          { type: "paragraph", spans: [{ text: "legal@get-carrot.com" }] }
        ]
      },
      clause: { label: "I have read and agree to the Notice of Privacy Practices." }
    },
    hipaa: {
      section: {
        heading: "Annual HIPAA authorization (optional)",
        body: [
          { type: "paragraph", spans: [{ text: "Carrot Medical of Delaware, LLC", bold: true }] },
          {
            type: "paragraph",
            spans: [{ text: "Authorization for Use and Disclosure of Protected Health Information" }]
          },
          { type: "paragraph", spans: [{ text: "45 CFR § 164.508" }] },
          { type: "paragraph", spans: [{ text: "1. Patient information", bold: true }] },
          { type: "paragraph", spans: [{ text: `My name: ${PATIENT_NAME_PLACEHOLDER}` }] },
          { type: "paragraph", spans: [{ text: "2. Who may use or disclose my health information", bold: true }] },
          {
            type: "paragraph",
            spans: [
              {
                text: 'Carrot Medical of Delaware, LLC ("Carrot Medical") may use and disclose My Health Information (identified below) under this Authorization.'
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: 'As used in this Authorization, "' },
              { text: "My Health Information", bold: true },
              {
                text: '" means my protected health information ("PHI") as defined under the Health Insurance Portability and Accountability Act of 1996 and its implementing regulations (collectively, "HIPAA").'
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "3. Recipients of my health information", bold: true }] },
          {
            type: "paragraph",
            spans: [
              { text: "I authorize Carrot Medical to disclose My Health Information to the following recipients:" }
            ]
          },
          {
            type: "list",
            items: [
              {
                spans: [
                  { text: "Carrot Fertility, Inc.", bold: true },
                  {
                    text: ' ("Carrot Fertility"), which provides management, administrative, billing, care coordination, technology platform, and operational services to Carrot Medical as its Management Services Organization and Business Associate;'
                  }
                ]
              },
              {
                spans: [
                  { text: "Carrot Fertility's subcontractors, agents, and vendors", bold: true },
                  {
                    text: " that support the delivery of management and administrative services, including but not limited to technology platform providers, electronic health record systems, telehealth infrastructure providers, scheduling and communication tools, billing and revenue cycle management vendors, and payment processors, each subject to appropriate data protection obligations;"
                  }
                ]
              },
              {
                spans: [
                  { text: "My treating providers and care team", bold: true },
                  {
                    text: ", including physicians, nurses, pharmacists, and other licensed health care professionals involved in my care at or through Carrot Medical, whether employed by or contracted with Carrot Medical;"
                  }
                ]
              },
              {
                spans: [
                  { text: "Specialty providers and referral recipients", bold: true },
                  {
                    text: " to whom Carrot Medical refers me for consultation, diagnosis, or treatment in connection with my care;"
                  }
                ]
              },
              {
                spans: [
                  { text: "Pharmacies and pharmacy benefit managers", bold: true },
                  {
                    text: " to the extent necessary to fill, refill, or administer prescriptions issued in connection with my care at Carrot Medical;"
                  }
                ]
              },
              {
                spans: [
                  { text: "Health plans, health insurance carriers, and insurers", bold: true },
                  {
                    text: " to the extent necessary for eligibility verification, prior authorization, claims submission, billing, and payment processing in connection with my care;"
                  }
                ]
              },
              {
                spans: [
                  { text: "Employers and plan sponsors", bold: true },
                  {
                    text: ' that sponsor or administer a health plan or health reimbursement arrangement ("HRA") through which I receive benefits, solely for plan administration purposes and as permitted by applicable law, including 45 CFR § 164.504(f);'
                  }
                ]
              },
              {
                spans: [
                  { text: "Third-party administrators and benefits administrators", bold: true },
                  {
                    text: " acting on behalf of my employer or plan sponsor in connection with the administration of my health benefits;"
                  }
                ]
              },
              {
                spans: [
                  { text: 'Health information exchanges ("HIEs")', bold: true },
                  {
                    text: " and interoperability networks in which Carrot Medical participates, for treatment, payment, health care operations, and public health purposes, subject to my right to opt out as described in the Carrot Medical Notice of Privacy Practices;"
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "Research institutions, academic medical centers, and clinical research organizations",
                    bold: true
                  },
                  {
                    text: ', solely pursuant to a separate research authorization or an applicable Institutional Review Board ("IRB") waiver, and only to the extent I have separately authorized such disclosure or such waiver applies;'
                  }
                ]
              },
              {
                spans: [
                  { text: "Quality improvement and accreditation organizations", bold: true },
                  {
                    text: ", including entities conducting reviews of Carrot Medical's health care operations for purposes of quality assessment, patient safety, or accreditation; and"
                  }
                ]
              },
              {
                spans: [
                  { text: "Other recipients", bold: true },
                  {
                    text: " as required or permitted by applicable law, including public health authorities, law enforcement agencies, health oversight agencies, medical examiners, and courts or administrative bodies in response to a lawful order or subpoena."
                  }
                ]
              }
            ]
          },
          {
            type: "paragraph",
            spans: [{ text: "4. My health information covered by this authorization", bold: true }]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "I authorize Carrot Medical to use and disclose the following categories of My Health Information:"
              }
            ]
          },
          {
            type: "list",
            items: [
              {
                spans: [
                  {
                    text: "My medical records, including all information about reproductive health, fertility care, and hormonal health;"
                  }
                ]
              },
              {
                spans: [{ text: "My billing and payment records generated in connection with care at Carrot Medical;" }]
              },
              {
                spans: [
                  {
                    text: "Appointment and scheduling records, including visit history, cancellations, and no-show information;"
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "Communications between me and my care team at Carrot Medical, including messages, telehealth session records, and clinical notes;"
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "Prescription and medication records, including medications prescribed, dispensed, or administered in connection with my care; and"
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "Insurance, eligibility, and benefits information submitted to or received from health plans or plan sponsors in connection with my care."
                  }
                ],
                blocks: [
                  {
                    type: "paragraph",
                    spans: [
                      {
                        text: "In addition, without limiting the categories above, I specifically authorize the disclosure of the following categories of highly confidential health information included in the records identified above, to the extent such information is present in my records:"
                      }
                    ]
                  }
                ]
              },
              { spans: [{ text: "Genetic test results and other genetic information; and" }] },
              { spans: [{ text: "Information about mental health diagnosis and treatment." }] }
            ]
          },
          { type: "paragraph", spans: [{ text: "5. Purpose of this authorization", bold: true }] },
          {
            type: "paragraph",
            spans: [{ text: "I authorize the use and disclosure of My Health Information for the following purposes:" }]
          },
          {
            type: "list",
            items: [
              {
                spans: [
                  {
                    text: "To allow Carrot Fertility and its subcontractors to perform management, administrative, billing, care coordination, technology platform, and quality improvement services on behalf of Carrot Medical;"
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "To facilitate the administration of health benefits, including HRA reimbursement, health plan claims, and eligibility verification, by my employer, plan sponsor, health plan, or their respective administrators;"
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "To enable Carrot Medical and Carrot Fertility to develop, operate, maintain, and improve the technology platform and clinical services through which I receive care, using de-identified or aggregated information where possible;"
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "To support care coordination, referrals, and transitions of care between Carrot Medical and other treating providers involved in my care;"
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "To allow pharmacies and pharmacy benefit managers to fill and administer prescriptions issued in connection with my care;"
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "To support quality improvement, patient safety, and outcomes measurement activities conducted by or on behalf of Carrot Medical;"
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "To allow Carrot Medical or Carrot Fertility to contact me with information about health care services, treatment options, wellness programs, or related offerings that may be relevant to my care — I understand I may opt out of such communications at any time; and"
                  }
                ]
              },
              {
                spans: [
                  {
                    text: "To fulfill any other purpose specifically described to me and agreed to in writing at the time of disclosure."
                  }
                ]
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "6. Term of authorization", bold: true }] },
          {
            type: "paragraph",
            spans: [
              {
                text: "This Authorization is effective on the date I provide my consent and expires one year from that date.*"
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "* If a law requires this Authorization to expire prior to the completion of the above period, then this Authorization will expire on such earlier date."
              }
            ]
          },
          { type: "paragraph", spans: [{ text: "7. Your rights", bold: true }] },
          {
            type: "paragraph",
            spans: [
              { text: "Right to revoke.", bold: true },
              {
                text: " I understand that I may revoke this Authorization at any time by contacting our HIPAA Compliance Officer at:"
              }
            ]
          },
          {
            type: "paragraph",
            spans: [{ text: "Carrot Medical of Delaware, LLC, HIPAA Compliance Officer · legal@get-carrot.com" }]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "Revocation will not affect any uses or disclosures already made in reliance on this Authorization before Carrot Medical received my written notice."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Treatment is not conditioned on signing.", bold: true },
              {
                text: " This Authorization is voluntary. Carrot Medical will not refuse to provide me with treatment, and will not condition payment, enrollment, or eligibility for benefits, based on whether I sign this Authorization. I may proceed without agreeing to this Authorization; however, to allow Carrot Fertility to perform administrative and management services on behalf of Carrot Medical, I must agree to this Authorization."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Information may be redisclosed.", bold: true },
              {
                text: " I understand that once My Health Information is disclosed under this Authorization, it may be redisclosed by the recipient and may no longer be protected by HIPAA."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              { text: "Copy of this Authorization.", bold: true },
              { text: " I understand that I have a right to a copy of this Authorization." }
            ]
          },
          { type: "paragraph", spans: [{ text: "8. Scope and effect of this authorization", bold: true }] },
          {
            type: "paragraph",
            spans: [
              {
                text: "This Authorization applies to the uses and disclosures of My Health Information described above, including circumstances in which my written authorization is required under HIPAA or is otherwise obtained by Carrot Medical. It does not expand, restrict, or otherwise affect Carrot Medical’s ability to use or disclose My Health Information as otherwise permitted or required by HIPAA or other applicable law without my authorization, including for treatment, payment, and health care operations."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "The purposes, types of uses and disclosures, and categories of recipients described in this Authorization are not intended to identify every circumstance in which Carrot Medical may lawfully use or disclose My Health Information. If I do not authorize a particular use or disclosure under this Authorization, that does not prevent Carrot Medical from making the same or a similar use or disclosure when it is otherwise permitted or required by HIPAA or other applicable law without my authorization."
              }
            ]
          },
          {
            type: "paragraph",
            spans: [
              {
                text: "Similarly, the inclusion of a particular use or disclosure in this Authorization does not necessarily mean that my authorization is legally required for that use or disclosure. Carrot Medical may rely on another lawful basis under HIPAA or applicable law when one is available."
              }
            ]
          }
        ]
      },
      clause: {
        label: "I authorize the use and disclosure of my health information as outlined in this document. (optional)"
      }
    }
  },
  signature: {
    label: "Type your full name to sign and consent"
  }
}

// Every consent type the flow knows about — the keys of `consents`, so it can't drift from the copy.
// Safe fallback when the server's per-consent status is unavailable: treat all as outstanding.
export const consentTypes = Object.keys(consentDocument.consents) as ConsentType[]
